import { ChartDataXY, ChartSeriesXY } from '../data/chart-data-types';
import { customElement, property } from 'lit/decorators.js';
import { DashboardWidget } from './dashboard-widget';
import { DateRange } from './dashboard-filter';
import { generatedPurchaseOrdersCache } from '../data/generated-purchase-orders-cache';
import { html, PropertyValues } from 'lit';
import {
  localDateTimeToServer,
  serverDateTimeToLocalDateTime
} from '../../../../webmodule-common/other/datetime-converter';
import { TimelineWidgetModal } from './timeline-widget-modal';
import { tlang } from '../../../../webmodule-common/other/language/lang';
import { ViewDailyRevenue } from '../../../api/dealer-api-interface-franchisee';
import type { Snippet } from '../../../../webmodule-common/interop/webmodule-interop';

const ApexCharts = (globalThis as any).ApexCharts;

@customElement('generated-purchase-orders-revenue-widget')
export class GeneratedPurchaseOrdersWidget extends DashboardWidget {
  chart: any | null = null;

  @property() dateRange?: DateRange;
  @property() branchId?: string;
  @property() userId?: string;

  private _cache = generatedPurchaseOrdersCache();

  protected async refreshData(): Promise<ViewDailyRevenue[]> {
    if (this.dateRange) {
      const startDate = localDateTimeToServer(this.dateRange.startDate);
      const endDate = localDateTimeToServer(this.dateRange.endDate);
      const result = await this._cache?.getData(startDate, endDate, this.branchId, this.userId);
      if (result) {
        return result.revenueSummary;
      }
    }
    return [];
  }

  protected firstUpdated(): void {
    const options = {
      chart: {
        type: 'area',
        height: '100%',
        selection: {
          enabled: false
        },
        toolbar: {
          tools: {
            download: false,
            customIcons: [
              {
                icon: '<i class="fa fa-plus-circle fa-lg"></i>',
                index: 0,
                title: 'Zoom',
                class: 'custom-icon',
                click: (chart: any, options: any, e: any) => this.onChartClick(e, chart, options)
              }
            ]
          }
        },
        zoom: {
          enabled: false
        }
      },
      stroke: {
        width: 1
      },
      dataLabels: {
        enabled: false
      },
      legend: { show: false },
      tooltip: {
        enabled: false
      },
      states: {
        hover: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        active: {
          filter: {
            type: 'none',
            value: 0
          }
        }
      },
      series: [
        {
          name: tlang`%%purchase-order%% Revenue`,
          data: []
        }
      ],
      xaxis: {
        type: 'datetime'
      },
      yaxis: {
        labels: {
          formatter: (value: number) => this.getYAxisLabel(value)
        }
      },
      noData: {
        text: 'Loading...'
      }
    };

    this.chart = new ApexCharts(this.querySelector('.purchase-order-issued-revenue'), options);
    this.chart.render();
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    this.updateData();
  }

  private getTitle(): string {
    return tlang`!!purchase-order!! Generated - ${this.dateRange?.label}`;
  }

  protected getHeader(): Snippet {
    return this.getTitle();
  }

  protected getBody(): Snippet {
    return html`<div class="purchase-order-issued-revenue"></div>`;
  }

  protected getYAxisLabel(value: number): string {
    return `$${Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value)}`;
  }

  protected async onChartClick(_event: any, _chartContext: any, config: any) {
    const series = config.config.series as ChartSeriesXY[];
    const modal = new TimelineWidgetModal();
    modal.title = this.getTitle();
    modal.seriesData = series;
    await modal.showModal();
  }

  protected updateData(): void {
    const formatDate = (s: string) => {
      let dt = serverDateTimeToLocalDateTime(s);
      dt = dt.set({ hour: 0, minute: 0 });
      return dt.toMillis();
    };

    this.refreshData().then(results => {
      const chartData: ChartDataXY[] = [];
      results.map(r =>
        chartData.push({
          x: formatDate(r.date),
          y: r.dailyRevenue
        })
      );
      this.chart.updateSeries(
        [
          {
            name: tlang`%%quote%% Revenue`,
            data: chartData
          }
        ],
        true
      );
    });
  }
}
